import { useNavigate } from "react-router-dom";

import { PermissionsEnum } from "@/auth/interfaces";
import {
  EstadosPreagendamientoEnumChoice,
  ModulosEnumChoice,
  ROUTER_PATHS,
} from "@/shared";
import {
  getPreagendamiento,
  PreagendamientoOcupado,
  useFetchPreagendamientos,
  useUpdatePreagendamiento,
} from "@/shared/actions/erp";
import { GeneralTable, TableTabsScene } from "@/shared/components/ui";
import { TABLE_CONSTANTS } from "@/shared/constants/ui";
import {
  useCheckPermissions,
  useTableServerSideFiltering,
} from "@/shared/hooks";
import { useTableFilter } from "@/shared/hooks/useTableFilter";
import {
  AlertSeverityEnum,
  IDataBitacoraRespById,
  IPreagendamiento,
} from "@/shared/interfaces";
import { hasPermission } from "@/shared/utils";
import { useAuthStore } from "@/store/auth";
import { useDiccionarioMotivo } from "@/store/erp/diccionario_motivo/diccionario_motivo.store";
import { useUiStore } from "@/store/ui";
import { Edit, LockOpen } from "@mui/icons-material";
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import { Grid, IconButton, Tooltip } from "@mui/material";
import { useColumnsPreagendamientos } from "../../shared/hooks";

export type PreagendamientoPendientePageEstadoAceptacionProps = {
  estado_aceptacion?: boolean;
};

const PreagendamientoPendientePageEstadoAceptacion: React.FC<
  PreagendamientoPendientePageEstadoAceptacionProps
> = ({ estado_aceptacion = false }) => {
  //PERMISSIONS
  useCheckPermissions(PermissionsEnum.api_view_preagendamiento);
  const isPyLAdmin = hasPermission(
    PermissionsEnum.pyl_administrator_permission
  );

  //CONST
  const returnUrlAgendamientosPyLPage =
    ROUTER_PATHS.operaciones.agendamientoPyLNav;
  const navigate = useNavigate();

  //GLOBALS
  const setAlert = useUiStore((s) => s.setAlert);
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  const user = useAuthStore((s) => s.user);

  const {
    socket,
    setOpen,
    setOpenCreaBitacora,
    setFaseVenta,
    setDetalle,
    emitGetVentaById,
  } = useDiccionarioMotivo();

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: PreagendamientosPagingRes,
    isLoading,
    isRefetching,
  } = useFetchPreagendamientos({
    enabled: true,
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      estado_aceptacion: estado_aceptacion,
      identificacion: searchTerm,
      ...filterObject,
      filterByState: false,
      estado_preagendamiento_in: `${EstadosPreagendamientoEnumChoice.EN_ESPERA},${EstadosPreagendamientoEnumChoice.EN_PAUSA}`,
      tipo_orden_servicio_exclude: 'TRASLADO',
    },
  });

  ///* mutations --------------------
  const updatePreAgendamientoMutationOcupado =
    useUpdatePreagendamiento<PreagendamientoOcupado>({
      enableRedirect: false,
      enableSuccessAlert: false,
    });

  ///* handlers
  const onEdit = async (preagendamiento: IPreagendamiento) => {
    const selectedPreagendamiento = await getPreagendamiento(
      preagendamiento.id_preagendamiento!
    );
    //valido que exista en preagendamientos
    if (!selectedPreagendamiento)
      return setAlert(
        true,
        "No se puede acceder a este registro. Por favor, recargue la página.",
        AlertSeverityEnum.info
      );

    //obtengo el usuario que se encuentra gestionando el preagendamiento
    const actualUser = selectedPreagendamiento?.data?.user_gestionando;

    if (actualUser != null && actualUser != user?.username)
      return setAlert(
        true,
        "No puede acceder a este preagendamiento porque ya se encuentra siendo procesado.",
        AlertSeverityEnum.info
      );

    setAlert(
      true,
      "En este momento solo usted puede modificar este registro.",
      AlertSeverityEnum.info
    );
    navigate(
      `${returnUrlAgendamientosPyLPage}/confirmar/${preagendamiento.id_preagendamiento}`
    );
    await updatePreAgendamientoMutationOcupado.mutateAsync({
      id: preagendamiento.id_preagendamiento!,
      data: {
        user_gestionando: user?.username,
      },
    });
  };

  const unlockPreagendamiento = async (preagendamiento: IPreagendamiento) => {
    const selectedPreagendamiento = await getPreagendamiento(
      preagendamiento.id_preagendamiento!
    );
    //valido que exista en preagendamientos
    if (!selectedPreagendamiento)
      return setAlert(
        true,
        "No se puede acceder a este registro. Por favor, recargue la página.",
        AlertSeverityEnum.info
      );

    //obtengo el usuario que se encuentra gestionando el preagendamiento
    const actualUser = selectedPreagendamiento?.data?.user_gestionando;
    if (actualUser == null)
      return setAlert(
        true,
        "El registro no esta siendo gestionado. Acceda normalmente.",
        AlertSeverityEnum.info
      );

    //si esta libre setea el usuario que esta actualmente logueado
    await updatePreAgendamientoMutationOcupado.mutateAsync({
      id: preagendamiento.id_preagendamiento!,
      data: {
        user_gestionando: null,
      },
    });

    setAlert(true, "Preagendamiento liberado.", AlertSeverityEnum.info);
  };

  const openBitacora = (value: IPreagendamiento) => {
    if (!socket) return;

    socket.emit(
      "getVentaById",
      {
        numero_referencia_venta: value?.id_preagendamiento,
      },
      (data: IDataBitacoraRespById) => {
        if (data.code == 200) {
          emitGetVentaById(value.id_preagendamiento!);
          setOpen(true);
          return;
        }

        const details = {
          numero_referencia_venta: value.id_preagendamiento,
          identificacion_cliente: value.prospecto_data.identificacion,
          nombre_cliente: value.prospecto_data.razon_social,
          canal_venta: value.prospecto_data.canal_venta_create,
          vendedor: value.prospecto_data.user_create,
          fecha_aceptacion: value.preventa_data.fecha_aceptacion,
        };
        setDetalle(details);
        setFaseVenta(ModulosEnumChoice.PREAGENDAMIENTO);
        setOpenCreaBitacora(true);
      }
    );
  };

  ///* columns
  const { preagendamientoPendingReviewColumnsOcupado } =
    useColumnsPreagendamientos();

  return (
    <TableTabsScene globalFilter={globalFilter} onChangeFilter={onChangeFilter}>
      <GeneralTable<IPreagendamiento>
        columns={preagendamientoPendingReviewColumnsOcupado}
        data={PreagendamientosPagingRes?.data?.data || []}
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // filters - server side
        enableManualFiltering={true}
        manualSorting={false}
        enableSorting={false}
        //onSortingChange={}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={PreagendamientosPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasPermission(
          PermissionsEnum.api_change_preagendamiento
        )}
        actionsColumnSize={TABLE_CONSTANTS.ACTIONCOLUMN_WIDTH}
        canEdit={false}
        canDelete={false}
        enableEditing={estado_aceptacion}
        showCustomActionBtnSpace={estado_aceptacion}
        customActionBtnSpace={(value) => {
          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Tooltip title="Crear Bitácora">
                  <IconButton onClick={() => openBitacora(value)}>
                    <ForumRoundedIcon />
                  </IconButton>
                </Tooltip>
                <IconButton onClick={() => onEdit(value)}>
                  <Edit />
                </IconButton>
                {isPyLAdmin ? (
                  <IconButton onClick={() => unlockPreagendamiento(value)}>
                    <LockOpen />
                  </IconButton>
                ) : null}
              </Grid>
            </Grid>
          );
        }}
      />
    </TableTabsScene>
  );
};

export default PreagendamientoPendientePageEstadoAceptacion;
